import React, { useEffect, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import io from "socket.io-client";

import PixAndGoHeader from "@/components/molecules/PixAndGoHeader";
import PixAndGoLayout from "@/components/molecules/PixAndGoLayout";
import PixAndGoStepper from "@/components/molecules/PixAndGoStepper";
import i18n from "@/translate/i18n";
import { getPixAndGoStatus } from "@/services/pixAndGoApi";

const FINAL_STATUSES = [
  "completed",
  "user_registration_failed",
  "deposit_failed",
];

function PixAndGoProcessing() {
  const history = useHistory();
  const location = useLocation();
  const { uuid } = useParams();

  const merchantSlug = location.state?.merchantSlug;
  const initialStatus = location.state?.status || "registering_user";

  const [currentStatus, setCurrentStatus] = useState(initialStatus);

  useEffect(() => {
    i18n.changeLanguage("pt-BR");
  }, []);

  useEffect(() => {
    getPixAndGoStatus(uuid)
      .then((res) => {
        const { status } = res.data;

        if (FINAL_STATUSES.includes(status)) {
          history.replace({
            pathname: `/pix-and-go/${uuid}/status`,
            state: { status, merchantSlug },
          });
          return;
        }

        if (status === "form_required") {
          history.replace({
            pathname: `/pix-and-go/${uuid}/form`,
            state: { merchantSlug },
          });
          return;
        }

        if (status === "deposit_in_progress") {
          setCurrentStatus("deposit_in_progress");
        }
      })
      .catch(() => {
        // Transient error — stay on page and let websocket handle final state
      });
  }, [uuid]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const socket = io(process.env.REACT_APP_SERVER_URL, {
      path: "/websocket/socket.io",
    });

    socket.on("confirmTransaction", (socketData) => {
      if (socketData.event !== "pix_and_go" || socketData.uuid !== uuid) return;

      const { status } = socketData;

      if (FINAL_STATUSES.includes(status)) {
        socket.disconnect();
        history.push({
          pathname: `/pix-and-go/${uuid}/status`,
          state: { status, merchantSlug },
        });
        return;
      }

      if (status === "deposit_in_progress") {
        setCurrentStatus("deposit_in_progress");
      }
    });

    return () => {
      socket.disconnect();
    };
  }, [uuid, history, merchantSlug]);

  const isDeposit = currentStatus === "deposit_in_progress";

  return (
    <PixAndGoLayout merchantSlug={merchantSlug}>
      <PixAndGoHeader />
      <PixAndGoStepper currentStep={4} />
      <main className="flex flex-col items-center justify-center flex-1 px-6 pb-12 sm:px-12">
        <div className="w-full max-w-lg text-center flex flex-col items-center gap-6">
          <div className="w-16 h-16 border-4 border-pag-primary border-t-transparent rounded-full animate-spin" />
          <h1 className="text-2xl font-bold text-pag-on-background">
            {i18n.t(
              isDeposit
                ? "pixAndGo.processing.deposit.title"
                : "pixAndGo.processing.registering.title",
            )}
          </h1>
          <p className="text-base text-pag-on-background-muted">
            {i18n.t(
              isDeposit
                ? "pixAndGo.processing.deposit.message"
                : "pixAndGo.processing.registering.message",
            )}
          </p>
        </div>
      </main>
    </PixAndGoLayout>
  );
}

export default PixAndGoProcessing;
